import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Col, Row } from 'reactstrap';

import { modelOf } from '../../../../prop-types';
import CheckoutStore from '../../../../store/CheckoutStore';
import { createForm } from '../../../../models/Form';
import { createFormField } from '../../../../models/FormField';
import {
  maxLength,
  minLength,
  notBlank,
} from '../../../../util/formValidators';
import FormFieldType from '../../../../types/FormFieldType';
import FormGroupField from '../../../form/FormGroupField';
import SelectField from '../../../form/SelectField';
import ShippingSelectionFieldType from '../../../../types/ShippingSelectionFieldType';

const CheckoutShippingSelectionFieldsForm = ({
  checkoutStore,
  onOrderUpdate,
}) => {
  const { cancelOrder, order } = checkoutStore;
  const module = order.selected_shipping?.module;
  const selectionFields = module?.selectionFields || [];

  const getFormFieldType = (selectionFieldType) => {
    switch (selectionFieldType) {
      case ShippingSelectionFieldType.SELECT:
        return FormFieldType.SELECT;
      case ShippingSelectionFieldType.TEXT:
      default:
        return FormFieldType.TEXT;
    }
  };

  const form = useMemo(() => {
    const formFields = {};

    const getValidators = (selectionField) => {
      const validators = [];
      const fieldLabel = selectionField.name;

      if (selectionField.required) {
        validators.push(notBlank(fieldLabel));
      }

      if (selectionField.min_length) {
        validators.push(minLength(fieldLabel, selectionField.min_length));
      }

      if (selectionField.max_length) {
        validators.push(maxLength(fieldLabel, selectionField.max_length));
      }

      return validators;
    };

    selectionFields.forEach((selectionField) => {
      const validators = getValidators(selectionField);
      const fieldId = `shipping_selection_field_${selectionField.id}`;

      // Get current value from selected_shipping if it exists
      let currentValue = order.selected_shipping?.selection_fields.get(
        selectionField.id
      )?.value;

      if (currentValue === null || currentValue === undefined) {
        currentValue =
          selectionField.value || selectionField.default_text || '';
      }

      currentValue =
        currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : '';

      formFields[fieldId] = createFormField(
        {
          id: fieldId,
          type: getFormFieldType(selectionField.type),
          value: currentValue,
          required: selectionField.required,
          min_length: selectionField.min_length,
          max_length: selectionField.max_length,
        },
        validators
      );
    });

    return createForm(formFields);
  }, [
    selectionFields.length,
    order.selected_shipping?.selection_fields.size,
    order.selected_shipping?.module?.id,
  ]);

  const handleFieldChange = (e, selectionFieldId) => {
    cancelOrder();
    const fieldId = `shipping_selection_field_${selectionFieldId}`;
    const formField = form.getField(fieldId);

    let value = e.target.value;

    formField.setValue(value);

    // Update selected_shipping selection_fields
    order.selected_shipping.updateSelectionField({
      id: selectionFieldId,
      value: value,
    });

    if (e.target.type !== 'text') {
      onOrderUpdate({ withoutTimer: true });
    }
  };

  const handleFieldBlur = () => {
    onOrderUpdate({ withoutTimer: true });
  };

  const renderField = (selectionField) => {
    const fieldId = `shipping_selection_field_${selectionField.id}`;
    const formField = form.getField(fieldId);

    if (!formField) {
      return null;
    }

    const description = selectionField.description ? (
      <div className="CheckoutShippingSelectionFieldsForm__description">
        {selectionField.description}
      </div>
    ) : null;

    // Render SELECT type
    if (selectionField.type === ShippingSelectionFieldType.SELECT) {
      const options = selectionField.options
        ? selectionField.options.map((option) => ({
            value: String(option.value),
            label: option.label,
          }))
        : [];

      // Add empty option if default_text exists
      if (selectionField.default_text) {
        options.unshift({
          value: '',
          label: selectionField.default_text,
        });
      }

      return (
        <Col key={selectionField.id} xs={12}>
          <SelectField
            field={formField}
            fieldName={fieldId}
            formName={'checkout'}
            floatingLabel
            label={selectionField.name}
            options={options}
            required={selectionField.required}
            onChange={(e) => handleFieldChange(e, selectionField.id)}
          />
          {description}
        </Col>
      );
    }

    // Render TEXT type (default)
    return (
      <Col key={selectionField.id} xs={12}>
        <FormGroupField
          field={formField}
          fieldName={fieldId}
          formName={'checkout'}
          floatingLabel
          label={selectionField.name}
          required={selectionField.required}
          maxLength={selectionField.max_length}
          minLength={selectionField.min_length}
          type="text"
          onChange={(e) => handleFieldChange(e, selectionField.id)}
          onBlur={handleFieldBlur}
        />
        {description}
      </Col>
    );
  };

  if (selectionFields.length === 0) {
    return null;
  }

  return (
    <div className="CheckoutShippingSelectionFieldsForm">
      <Row>
        {selectionFields.map((selectionField) => renderField(selectionField))}
      </Row>
    </div>
  );
};

CheckoutShippingSelectionFieldsForm.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  onOrderUpdate: PropTypes.func.isRequired,
};

export default inject('checkoutStore')(
  observer(CheckoutShippingSelectionFieldsForm)
);
